// @ts-nocheck
import React, { useState, useEffect } from 'react';
import KwPanel from '../../common/KwPanel';
import { toast } from 'react-toastify';
import { Date_AsString_Raw } from '../../../util/formatter';
import { hasAnyPermission } from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import { NavLink } from 'react-router-dom';
import Button from '../../common/Button';
import icons from '../../../constants/icons';
import { useTranslation } from 'react-i18next';
import {
  getAccountDetails,
  sendAccountDetailEmail,
} from '../../../api/requestApi';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { deleteAccountDetail } from '../../../reducers/basketReducer';
import Loading from '../../common/Loading';

export interface AccountDetailsTableProps {
  request: any;
}

function AccountDetailsTable({ request }: AccountDetailsTableProps) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [accountDetails, setAccountDetails] = useState<any[]>([]);
  const [showRemove, setShowRemove] = useState(false);

  const toggleShowRemove = () => {
    setShowRemove(!showRemove);
  };

  useEffect(() => {
    if (
      accountDetails.length === request.accountDetails ||
      (accountDetails.length && !request.accountDetails)
    ) {
      return;
    }

    setIsLoading(true);
    getAccountDetails(request.id)
      .then((data) => {
        setAccountDetails(data);
        setIsLoading(false);
      })
      .catch((e) => {
        toast.error(t('Failed to get account details'));
        setIsLoading(false);
      });
  }, [accountDetails, request, t]);

  const sendEmail = (accountDetail: any) => {
    return sendAccountDetailEmail(
      request.id,
      accountDetail.keyRequestAccountDetailsId
    )
      .then((data) => {
        setAccountDetails((prevState: any) =>
          prevState?.map((existing: any) =>
            existing.keyRequestAccountDetailsId ===
              data.keyRequestAccountDetailsId
              ? data
              : existing
          )
        );
      })
      .catch((e) => {
        toast.error(e.message || t('Failed to send email'));
      });
  };

  const handleSendEmail = (accountDetail: any) => {
    sendEmail(accountDetail).then(() => {
      toast.success(t('Email sent successfully'));
    });
  };

  const handleSendAllEmails = () => {
    const accountDetailsToSend = accountDetails?.filter(
      (item) => item.canSendEmail && !item.emailSentDate
    );
    let promises: Promise<any>[] = [];
    accountDetailsToSend.forEach((accountDetail) => {
      promises.push(sendEmail(accountDetail));
    });
    Promise.all(promises).then(() => {
      toast.success(t('Emails sent successfully'));
    });
  };

  const handleDelete = (accountDetail: any) => {
    dispatch(
      deleteAccountDetail({
        requestId: request.id,
        id: accountDetail.keyRequestAccountDetailsId,
      })
    )
      .unwrap()
      .then(() => {
        toast.success(t('Account removed'));
        setAccountDetails((prevState) =>
          prevState?.filter(
            (x: any) =>
              x.keyRequestAccountDetailsId !==
              accountDetail.keyRequestAccountDetailsId
          )
        );
      })
      .catch((e: any) => {
        toast.error(t('Failed to remove account'));
      });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (accountDetails.length === 0) {
    return null;
  }

  return (
    <KwPanel className="mt-4">
      <header className="flex-wrap row-gap-5">
        <h2 className="flex-grow-1 flex-shrink-0 flex-basis-100">
          {t('Account Details')} ({accountDetails.length}/{request.numberOfKeys}
          )
        </h2>
        <div className="d-flex flex-wrap col-gap-5 row-gap-5 flex-grow-1">
          <Button
            className="btn btn-default btn-xs-full"
            isDisabled={
              accountDetails?.filter(
                (item) => item.canSendEmail && !item.emailSentDate
              ).length === 0
            }
            onClick={handleSendAllEmails}
            text={t('Send all emails')}
          />
          {accountDetails?.filter((item) => !item.canSendEmail).length > 0 && (
            <Button
              className={`btn ${showRemove ? 'btn-default' : ''
                } btn-xs-full ms-auto me-md-3`}
              onClick={toggleShowRemove}
              icon={icons.DELETE}
              text={t('Remove')}
              hideTitle={true}
            />
          )}
        </div>
      </header>
      <div className="table-wrapper">
        <table className="table table-has-buttons jsDataTable width-100 space-bottom mt-4">
          <thead>
            <tr>
              <th>{t('Login')}</th>
              <th>{t('Name')}</th>
              <th>{t('Email address')}</th>
              <th>{t('Status')}</th>
              <th style={{ minWidth: '68px', width: '68px' }}></th>
            </tr>
          </thead>
          <tbody>
            {accountDetails?.map((item: any) => (
              <React.Fragment key={item.login}>
                <tr>
                  <td>
                    {hasAnyPermission(currentUser, [
                      permissionTypes.MANAGE_KEYS,
                      permissionTypes.KEYS_SEARCH,
                    ]) ? (
                      <NavLink to={`/keys/account/${item.accountDetailId}`}>
                        {item.login}
                      </NavLink>
                    ) : (
                      item.login
                    )}
                  </td>
                  <td>{item.name}</td>
                  <td>{item.emailAddress}</td>
                  <td>
                    {!item.canSendEmail ? (
                      item.emailAddress ? (
                        <span className="badge bg-info">
                          {t('Pending key')}
                        </span>
                      ) : (
                        <span className="badge bg-danger">
                          {t('No email address')}
                        </span>
                      )
                    ) : !item.emailSentDate ? (
                      <Button
                        className="btn btn-default"
                        onClick={() => handleSendEmail(item)}
                        text={t('Send email')}
                      />
                    ) : !item.keyDownloaded ? (
                      <span
                        className="badge bg-info"
                        data-tooltip={`Email sent: ${Date_AsString_Raw(
                          item.emailSentDate
                        )}`}
                      >
                        {t('Sent')}
                      </span>
                    ) : (
                      <span
                        className="badge bg-success"
                        data-tooltip={`Retrieved: ${Date_AsString_Raw(
                          item.keyDownloaded
                        )}`}
                      >
                        {t('Retrieved')}
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    {!item.canSendEmail && (
                      <Button
                        className="btn btn-default btn-sm btn-xs-full show-loading"
                        style={{
                          display: showRemove ? 'block' : 'none',
                        }}
                        onClick={() => handleDelete(item)}
                        icon={icons.DELETE}
                        text={t('Remove')}
                        hideTitle={true}
                      />
                    )}
                  </td>
                </tr>
                {item.notes && (
                  <tr>
                    <td colSpan={5}>
                      {t('Notes')}: {item.notes}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </KwPanel>
  );
}

export default AccountDetailsTable;
